import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { artistHubEnabled, fanPassReport } from "@/services/firebase/artistHub";
import { isMonth, recentMonths, reportToCsv, resolveTotals } from "@/lib/fanPass";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const int = (n: number) => n.toLocaleString("zh-TW");
const ntd = (n: number) => n.toLocaleString("zh-TW", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const price = (n: number) => n.toLocaleString("zh-TW", { minimumFractionDigits: 3, maximumFractionDigits: 4 });

function downloadText(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * B-23: 鐵粉月卡月報 (read-only; contract §1b `fanPassReport {month}`, staff).
 * Revenue is recognised when coins are deducted (join / renew). Unit price = that month's weighted paid-coin price;
 * when no purchase data exists the backend uses the configured estimate and flags `estimated`.
 * Quarterly settlement and per-payee totals come from scripts/reports/fan_pass_share/build-report.ts.
 */
export default function FanPassReportPage() {
  const months = recentMonths(13);
  const [month, setMonth] = useState(months[0]);
  const report = useQuery({
    queryKey: ["fan-pass-report", month],
    queryFn: () => fanPassReport(month),
    enabled: artistHubEnabled && isMonth(month),
    retry: false,
  });
  const rows = report.data?.rows ?? [];
  const totals = resolveTotals(rows, report.data?.totals ?? null);
  const anyEstimated = !!report.data?.estimated || rows.some((r) => r.estimated);

  return (
    <AdminPage>
      <AdminPageHeader
        title="鐵粉月卡月報"
        subtitle="每位藝人當月扣除的購買金幣與分潤（消耗制，扣款當下認列）。季結算與收款對象合計請用報表腳本。"
        actions={
          <Button
            variant="outline"
            disabled={!report.data || rows.length === 0}
            onClick={() => downloadText(`fan_pass_report_${month}.csv`, reportToCsv(month, rows, totals))}
          >
            <Download className="mr-2 h-4 w-4" />
            匯出 CSV
          </Button>
        }
      />
      {!artistHubEnabled && <p className="text-sm text-muted-foreground">此環境尚未開放藝人專區。</p>}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="fan-pass-month">月份</Label>
          <select
            id="fan-pass-month"
            className="mt-1 block h-9 rounded-md border bg-background px-3 text-sm"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <Button variant="outline" disabled={report.isFetching || !artistHubEnabled} onClick={() => void report.refetch()}>
          {report.isFetching ? "讀取中…" : "重新整理"}
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        毛收入＝扣除購買金幣 × 單價；平台費＝毛收入 × 30%；可分配＝毛收入 × 70%；創作者＝可分配 × 收款資料比例（預設 70%）；平台＝可分配 − 創作者。
      </p>
      {anyEstimated && (
        <p role="note" className="mb-4 rounded bg-amber-50 p-3 text-sm text-amber-900">
          標示「預估」的列：當月沒有購買金幣入帳資料，單價採設定的預估值，結算前需以實際加權單價重算。
        </p>
      )}
      {report.isPending && artistHubEnabled && <p>讀取報表…</p>}
      {report.isError && (
        <p role="alert" className="text-destructive">
          讀取失敗：{report.error instanceof Error ? report.error.message : "請重試"}
        </p>
      )}
      {report.data && rows.length === 0 && <p className="text-sm text-muted-foreground">{month} 沒有任何鐵粉月卡扣款。</p>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-2 pr-3">藝人</th>
                <th className="py-2 pr-3 text-right">新開通</th>
                <th className="py-2 pr-3 text-right">續期</th>
                <th className="py-2 pr-3 text-right">購買金幣</th>
                <th className="py-2 pr-3 text-right">單價 NTD/枚</th>
                <th className="py-2 pr-3 text-right">毛收入</th>
                <th className="py-2 pr-3 text-right">平台費 30%</th>
                <th className="py-2 pr-3 text-right">創作者分潤</th>
                <th className="py-2 text-right">平台分潤</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.artistId} className="border-t">
                  <td className="py-2 pr-3">
                    {r.displayName || "—"}
                    <span className="block text-xs text-muted-foreground">{r.artistId}</span>
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{int(r.joins)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{int(r.renews)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{int(r.paidCoins)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {price(r.unitPriceNtd)}
                    {r.estimated && <span className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-900">預估</span>}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{ntd(r.grossNtd)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{ntd(r.platformFeeNtd)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium">{ntd(r.creatorShareNtd)}</td>
                  <td className="py-2 text-right tabular-nums">{ntd(r.platformShareNtd)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-semibold">
                <td className="py-2 pr-3">合計（{rows.length} 位）</td>
                <td className="py-2 pr-3 text-right tabular-nums">{int(totals.joins)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{int(totals.renews)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{int(totals.paidCoins)}</td>
                <td className="py-2 pr-3" />
                <td className="py-2 pr-3 text-right tabular-nums">{ntd(totals.grossNtd)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{ntd(totals.platformFeeNtd)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{ntd(totals.creatorShareNtd)}</td>
                <td className="py-2 text-right tabular-nums">{ntd(totals.platformShareNtd)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </AdminPage>
  );
}
